import { Component, OnInit, QueryList, signal, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { NbToastrService } from '@nebular/theme';
import {
  BuildingDetailDto,
  BuildingService,
  CustomFieldCollectionDetailDto,
  CustomFieldCollectionInstanceMergeDto,
  CustomFieldCollectionService,
  ProtocolMergeDto,
  ProtocolService,
  ProtocolTypeItemDto,
  ProtocolTypeService,
} from '@artemis-software/wr-api';
import { CustomFormComponent } from '@shared/custom-form/custom-form/custom-form.component';
import { validateCustomForms } from '@shared/custom-form/custom-form-submit';
import { PendingChanges } from '@guards/pending-changes.guard';

export interface CreationSection {
  collection: CustomFieldCollectionDetailDto;
  expanded: boolean;
}

@Component({
  standalone: false,
  selector: 'wr-protocol-creation-page',
  templateUrl: './protocol-creation-page.component.html',
  styleUrls: ['./protocol-creation-page.component.scss'],
})
export class ProtocolCreationPageComponent implements OnInit, PendingChanges {

  @ViewChildren(CustomFormComponent)
  formComponents!: QueryList<CustomFormComponent>;

  readonly saving$ = new BehaviorSubject(false);
  readonly loading$ = new BehaviorSubject(true);
  readonly sections = signal<CreationSection[]>([]);
  readonly protocolType = signal<ProtocolTypeItemDto | undefined>(undefined);
  readonly building = signal<BuildingDetailDto | undefined>(undefined);

  private protocolTypeId: string | null = null;
  private sourceBuildingId: string | null = null;
  private sourceBuildingEquipmentId: string | null = null;
  private submitted = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly protocolService: ProtocolService,
    private readonly protocolTypeService: ProtocolTypeService,
    private readonly collectionService: CustomFieldCollectionService,
    private readonly buildingService: BuildingService,
    private readonly toastrService: NbToastrService,
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(async params => {
      const protocolTypeId = params['protocolTypeId'] as string | undefined;
      const buildingId = params['buildingId'] as string | undefined;
      const buildingEquipmentId = params['buildingEquipmentId'] as string | undefined;

      if (!protocolTypeId) {
        this.router.navigate(['protocols']);
        return;
      }

      this.protocolTypeId = protocolTypeId;
      this.sourceBuildingId = buildingId ?? null;
      this.sourceBuildingEquipmentId = buildingEquipmentId ?? null;

      await this.loadData(protocolTypeId, buildingId, buildingEquipmentId);
    });
  }

  async loadData(protocolTypeId: string, buildingId?: string, buildingEquipmentId?: string): Promise<void> {
    try {
      this.loading$.next(true);

      const [types] = await Promise.all([
        firstValueFrom(this.protocolTypeService.findAll({})),
      ]);
      const matchingType = types.find(t => t.id === protocolTypeId);
      this.protocolType.set(matchingType);

      if (buildingId) {
        try {
          const building = await firstValueFrom(this.buildingService.findById(buildingId));
          this.building.set(building);
        } catch {
          this.building.set(undefined);
        }
      }

      const preset = await firstValueFrom(
        this.protocolService.getPreset({ protocolTypeId }),
      );
      const sections: CreationSection[] = await Promise.all(
        preset.collections.map(async (col) => {
          const detail = await firstValueFrom(this.collectionService.findById(col.id));
          return { collection: detail, expanded: true };
        }),
      );
      this.sections.set(sections);
    } catch {
      this.toastrService.danger('Vorlage konnte nicht geladen werden.', 'Fehler');
    } finally {
      this.loading$.next(false);
    }
  }

  toggleExpand(index: number): void {
    this.sections.update(sections =>
      sections.map((s, i) => i === index ? { ...s, expanded: !s.expanded } : s),
    );
  }

  expandAll(): void {
    this.sections.update(sections => sections.map(s => ({ ...s, expanded: true })));
  }

  collapseAll(): void {
    this.sections.update(sections => sections.map(s => ({ ...s, expanded: false })));
  }

  navigateBack(): void {
    if (this.sourceBuildingId) {
      this.router.navigate(['building', this.sourceBuildingId], { queryParams: { tab: 'Protokolle' } });
    } else {
      this.router.navigate(['protocols']);
    }
  }

  async submit(): Promise<void> {
    const forms = this.formComponents.toArray();
    const validation = validateCustomForms(forms);
    if (validation.status === 'not-ready') {
      this.toastrService.danger('Formular ist noch nicht bereit.', 'Fehler');
      return;
    }
    if (validation.status === 'invalid') {
      this.toastrService.danger(validation.message, validation.title);
      return;
    }
    try {
      this.saving$.next(true);
      const instances: CustomFieldCollectionInstanceMergeDto[] = forms.map(form => form.getMergeDto());
      const mergeDto: ProtocolMergeDto = {
        protocolTypeId: this.protocolTypeId ?? undefined,
        buildingId: this.sourceBuildingId ?? undefined,
        buildingEquipmentId: this.sourceBuildingEquipmentId ?? undefined,
        instances,
      };
      const saved = await firstValueFrom(this.protocolService.merge(mergeDto));
      this.submitted = true;
      this.toastrService.success('Protokoll erstellt und archiviert', 'Erfolg');
      this.router.navigate(['protocols', saved.id], { replaceUrl: true });
    } catch {
      this.toastrService.danger('Protokoll konnte nicht erstellt werden.', 'Fehler');
    } finally {
      this.saving$.next(false);
    }
  }

  typeBadgeStyle(color: string | undefined): object {
    if (!color) return {};
    return {
      background: color + '22',
      border: `1.5px solid ${color}`,
      color: color,
    };
  }

  getHeaderBg(color: string | undefined): string {
    if (!color || color.length < 7) return '';
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, 0.12)`;
  }

  hasRequiredFields(section: CreationSection): boolean {
    return section.collection.members?.some(m => m.required) ?? false;
  }

  hasPendingChanges(): boolean {
    if (this.submitted) return false;
    return this.sections().length > 0;
  }
}
